import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Visible `<tr>` rows skip liquids that hide try-on / shipping-protection lines, but
   * remove + qty still use Shopify's 1-based line index — `cartItemRows[line - 1]` then
   * targets the wrong row or `undefined`.
   *
   * @param {number | string} line - Shopify cart line (1-based), from `forloop.index`.
   * @returns {HTMLTableRowElement | undefined}
   */
  #cartRowForShopifyLine(line) {
    const n = Number(line);
    if (!Number.isFinite(n) || n < 1) return undefined;
    const byAttr = this.querySelector(`tr.cart-items__table-row[data-cart-line="${n}"]`);
    if (byAttr instanceof HTMLTableRowElement) return byAttr;
    const rows = this.refs.cartItemRows;
    if (Array.isArray(rows) && rows[n - 1] instanceof HTMLTableRowElement) return rows[n - 1];
    return undefined;
  }

  /**
   * @param {number | string} line
   * @returns {Element | undefined}
   */
  #quantitySelectorForLine(line) {
    const row = this.#cartRowForShopifyLine(line);
    const inRow = row?.querySelector('quantity-selector-component');
    if (inRow) return inRow;
    const selectors = this.refs.quantitySelectors;
    const n = Number(line);
    if (Array.isArray(selectors) && Number.isFinite(n) && selectors[n - 1]) return selectors[n - 1];
    return undefined;
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.setPaymentIcons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  setPaymentIcons() {
    const wallet = document.querySelector('[wallet-configs]');
    if (!(wallet instanceof HTMLElement)) return;

    let cartQuickpay = [];
    try {
      cartQuickpay = JSON.parse(wallet.getAttribute('wallet-configs') || '[]');
    } catch {
      return;
    }
    if (!Array.isArray(cartQuickpay)) return;
    for (var s = 0; s < cartQuickpay.length; s++) {
      document.body.classList.add(`${cartQuickpay[s].name}`);
    }

    this.quickPayButton = this.querySelector('.expresspay');
    if (this.quickPayButton) {
      this.quickPayButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.showQuickPayOverlay(true);
      });
    }

    this.quickPayCloseButton = document.querySelector('.button-close-additional-checkout');
    if (this.quickPayCloseButton) {
      this.quickPayCloseButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.showQuickPayOverlay(false);
      });
    }
  }

  showQuickPayOverlay(show) {
    this.quickPayOverlay = document.querySelector('.additional-checkout-button__overlay');
    this.quickPayOverlay.classList.toggle('active', show);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#cartRowForShopifyLine(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#cartRowForShopifyLine(line);

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const _cartContext = this.closest('cart-drawer-component') ? 'mini-cart' : 'cart page';
    const _cartLabel = config.action === 'clear'
      ? `[Cart] remove item (${_cartContext})`
      : `[Cart] update quantity (${_cartContext})`;
    console.time(_cartLabel);

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    const { checkoutButton } = this.refs;
    const { checkoutSeparator } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    checkoutButton?.shimmer();
    checkoutSeparator?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        console.timeEnd(_cartLabel);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#quantitySelectorForLine(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.setPaymentIcons();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.setPaymentIcons();
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
